// Selection toolbar that appears above the Materials list when 1+
// materials are checked. Lets the operator reassign all selected
// materials to a single category (existing or inline-created) in one
// action. Calls `bulkUpdateMaterialCategory` from the manufacturing
// client; the parent owns refresh + selection-reset on success.
//
// The category control is a `ManufacturingComboboxPicker` (already
// proven on the receipt-review modal) so existing categories are
// searchable AND the operator can type a brand-new category name to
// create it inline -- categories are free-text on the material row
// (no separate category table), so "create" is literally just typing.
import { useState } from "react";
import { bulkUpdateMaterialCategory } from "@/lib/manufacturingClient";
import { ManufacturingComboboxPicker } from "./ManufacturingComboboxPicker";
import { InlineHelp } from "@/components/common/InlineHelp";

type Props = {
  selectedIds: ReadonlyArray<string>;
  existingCategories: ReadonlyArray<string>;
  busy: boolean;
  onClear: () => void;
  onApplied: (summary: { updated: number; failed: number }) => void;
};

const clearBtnClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";

const applyBtnClass =
  "rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

export function ManufacturingBulkCategoryToolbar({
  selectedIds,
  existingCategories,
  busy,
  onClear,
  onApplied,
}: Props) {
  const [draft, setDraft] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = draft.trim();
  const canApply = selectedIds.length > 0 && trimmed.length > 0 && !working;

  async function handleApply() {
    if (!canApply) return;
    setWorking(true);
    setError(null);
    const r = await bulkUpdateMaterialCategory({
      material_ids: [...selectedIds],
      category: trimmed,
    });
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onApplied({ updated: r.data.updated_count, failed: r.data.errors.length });
    setDraft("");
  }

  const options = existingCategories
    .filter((c) => c && c !== "all" && c.toLowerCase() !== "uncategorized")
    .map((c) => ({ key: c, label: c, data: c }));

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-2 dark:border-indigo-900 dark:bg-indigo-950/20">
      <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">
        {selectedIds.length} selected
      </span>
      <button type="button" className={clearBtnClass} onClick={onClear} disabled={working}>
        Clear
      </button>
      <span className="text-[11px] text-indigo-800 dark:text-indigo-300">Reassign category:</span>
      <InlineHelp
        label="How bulk category reassign works"
        dismissKey="mfg.bulkCategoryReassign"
      >
        Pick an existing category from the dropdown OR type a new name to create
        it inline (categories are free-text on the material row, so creating one
        just means saving any material with that string). The update is applied
        per material so each row gets its own audit entry.
      </InlineHelp>
      <div className="min-w-[180px] max-w-xs flex-1">
        <ManufacturingComboboxPicker<string>
          options={options}
          value={draft}
          onPick={(opt) => setDraft(opt.data)}
          onCreate={async (query) => {
            const name = query.trim();
            if (!name) return { error: "Type a category name" };
            return { option: { key: name, label: name, data: name } };
          }}
          createLabel="+ Create new category"
          placeholder="Pick or type a category…"
          disabled={working}
        />
      </div>
      {trimmed ? (
        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-zinc-900 dark:text-indigo-300">
          → {trimmed}
        </span>
      ) : null}
      <button
        type="button"
        className={applyBtnClass}
        onClick={() => void handleApply()}
        disabled={!canApply || busy}
      >
        {working ? "Applying…" : "Apply"}
      </button>
      {error ? (
        <span className="text-[11px] text-red-700 dark:text-red-300">{error}</span>
      ) : null}
    </div>
  );
}
